import { useEffect, useState, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { nettoyerNumeroWhatsapp } from "@/utils/whatsapp";
import { useProprietaires } from "../ProprietairesProvider";
import {
  PROPRIETAIRE_STATUT_OPTIONS,
  type Proprietaire,
  type ProprietaireInput,
  type ProprietaireStatut,
} from "../types";

interface ProprietaireFormProps {
  open: boolean;
  onClose: () => void;
  initial?: Proprietaire;
}

interface FormState {
  prenom: string;
  nom: string;
  telephone: string;
  whatsapp: string;
  email: string;
  adresse: string;
  notes: string;
  statut: ProprietaireStatut;
}

const EMPTY_STATE: FormState = {
  prenom: "",
  nom: "",
  telephone: "",
  whatsapp: "",
  email: "",
  adresse: "",
  notes: "",
  statut: "actif",
};

export default function ProprietaireForm({ open, onClose, initial }: ProprietaireFormProps) {
  const { addProprietaire, updateProprietaire } = useProprietaires();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        prenom: initial.prenom,
        nom: initial.nom,
        telephone: initial.telephone,
        whatsapp: initial.whatsapp,
        email: initial.email,
        adresse: initial.adresse,
        notes: initial.notes,
        statut: initial.statut,
      });
    } else {
      setForm(EMPTY_STATE);
    }
    setErrors({});
  }, [open, initial]);

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.prenom.trim()) nextErrors.prenom = "Le prénom est obligatoire.";
    if (!form.nom.trim()) nextErrors.nom = "Le nom est obligatoire.";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = "Indiquez une adresse email valide.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: ProprietaireInput = {
      prenom: form.prenom.trim(),
      nom: form.nom.trim(),
      telephone: form.telephone.trim(),
      whatsapp: nettoyerNumeroWhatsapp(form.whatsapp),
      email: form.email.trim(),
      adresse: form.adresse.trim(),
      notes: form.notes.trim(),
      statut: form.statut,
    };

    if (initial) {
      updateProprietaire(initial.id, input);
    } else {
      addProprietaire(input);
    }
    resetAndClose();
  };

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={initial ? "Modifier le propriétaire" : "Ajouter un propriétaire"}
      description="Renseignez la fiche du propriétaire. Le prénom et le nom sont obligatoires."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="proprietaire-form"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer le propriétaire
          </button>
        </>
      }
    >
      <form id="proprietaire-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field label="Prénom" htmlFor="prop-prenom" required error={errors.prenom}>
            <Input
              id="prop-prenom"
              value={form.prenom}
              onChange={(event) => update("prenom", event.target.value)}
              placeholder="Ex. Salif"
              maxLength={60}
              invalid={Boolean(errors.prenom)}
            />
          </Field>

          <Field label="Nom" htmlFor="prop-nom" required error={errors.nom}>
            <Input
              id="prop-nom"
              value={form.nom}
              onChange={(event) => update("nom", event.target.value)}
              placeholder="Ex. Ouédraogo"
              maxLength={60}
              invalid={Boolean(errors.nom)}
            />
          </Field>

          <Field label="Téléphone" htmlFor="prop-telephone">
            <Input
              id="prop-telephone"
              type="tel"
              value={form.telephone}
              onChange={(event) => update("telephone", event.target.value)}
              placeholder="Ex. +226 70 00 00 00"
              maxLength={40}
            />
          </Field>

          <Field
            label="Téléphone WhatsApp"
            htmlFor="prop-whatsapp"
            hint="Facultatif. Format international, ex. +226 70 00 00 00."
          >
            <Input
              id="prop-whatsapp"
              type="tel"
              value={form.whatsapp}
              onChange={(event) => update("whatsapp", event.target.value)}
              placeholder="+226 XX XX XX XX"
              maxLength={40}
            />
          </Field>

          <Field label="Email" htmlFor="prop-email" error={errors.email}>
            <Input
              id="prop-email"
              type="email"
              value={form.email}
              onChange={(event) => update("email", event.target.value)}
              placeholder="Ex. salif@exemple.bf"
              maxLength={120}
              invalid={Boolean(errors.email)}
            />
          </Field>

          <Field label="Statut" htmlFor="prop-statut" hint="Un propriétaire inactif reste consultable mais n'est plus suivi.">
            <Select
              id="prop-statut"
              value={form.statut}
              onChange={(event) => update("statut", event.target.value)}
              options={PROPRIETAIRE_STATUT_OPTIONS}
            />
          </Field>

          <Field label="Adresse" htmlFor="prop-adresse" className="sm:col-span-2">
            <Input
              id="prop-adresse"
              value={form.adresse}
              onChange={(event) => update("adresse", event.target.value)}
              placeholder="Adresse du propriétaire"
              maxLength={160}
            />
          </Field>

          <Field label="Notes" htmlFor="prop-notes" className="sm:col-span-2">
            <Textarea
              id="prop-notes"
              value={form.notes}
              onChange={(event) => update("notes", event.target.value)}
              placeholder="Remarques, préférences de gestion, mode de reversement des loyers..."
              className="min-h-[96px]"
              maxLength={600}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}